import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';

/**
 * OnboardingTooltip - A guided tour component for first-time users
 * 
 * Usage:
 * <OnboardingTooltip
 *   storageKey="client_onboarding_seen"
 *   steps={[
 *     { title: "Bienvenue!", message: "Ceci est votre dashboard", icon: Home },
 *     { title: "Catalogue", message: "Parcourez nos produits ici", icon: Package }
 *   ]}
 * />
 */

const OnboardingTooltip = ({ storageKey, steps, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already seen the onboarding
    const hasSeenOnboarding = localStorage.getItem(storageKey);
    if (!hasSeenOnboarding) {
      // Small delay to let the page render first
      setTimeout(() => setIsVisible(true), 500);
    }
  }, [storageKey]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
  };

  if (!isVisible || !steps || steps.length === 0) return null;

  const step = steps[currentStep];
  const StepIcon = step.icon || Lightbulb;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] animate-fade-in"
        onClick={handleSkip}
      />
      
      {/* Tooltip Card */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90%] max-w-md animate-slide-up">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className={`${step.bgColor || 'bg-gradient-to-r from-sonangol-red to-sonangol-orange'} p-6 text-white relative`}>
            <button 
              onClick={handleSkip}
              className="absolute top-2 right-2 w-11 h-11 flex items-center justify-center text-white/80 hover:text-white transition-colors"
              aria-label="Fechar"
              data-testid="onboarding-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <StepIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-white/80 text-sm">Passo {currentStep + 1} de {steps.length}</p>
                <h3 className="font-barlow text-xl font-bold">{step.title}</h3>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className="text-zinc-600 leading-relaxed">{step.message}</p>
            
            {step.tips && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{step.tips}</span>
                </p>
              </div>
            )}
          </div>
          
          {/* Progress dots */}
          <div className="flex justify-center gap-2 pb-4">
            {steps.map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep ? 'bg-sonangol-red w-6' : 'bg-zinc-300'
                }`}
              />
            ))}
          </div>
          
          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={handlePrev}
                className="flex-1 h-12 border-zinc-300"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              className={`flex-1 h-12 ${step.btnColor || 'bg-sonangol-red hover:bg-red-700'} text-white font-barlow font-bold`}
            >
              {isLastStep ? 'Começar!' : 'Próximo'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
          
          {/* Skip link */}
          <div className="pb-4 text-center">
            <button 
              onClick={handleSkip}
              className="px-4 py-3 text-zinc-400 text-sm hover:text-zinc-600 transition-colors"
              data-testid="onboarding-skip-btn"
            >
              Saltar introdução
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingTooltip;
